import { attachmentSchema } from "@/api/attachment";
import { multilingualFieldSchema, supportedLocales } from "@/i18n";
import { z } from "zod";
import { emptyToNullString } from "../types";

export const cmsPageTypes = [
  "catalogueRaisonne",
  "catalogueRaisonneGeneral",
  "selection",
  "news",
  "expertise",
  "aboutUs",
  "contact",
  "homepage",
  "login",
  "signup",
  "artist",
  "privacyPolicy",
  "legalNotices",
  "gcu",
] as const;

export const cmsPageTypeSchema = z.enum(cmsPageTypes);

export type CmsPageType = z.infer<typeof cmsPageTypeSchema>;

export interface CmsPageDto<C> {
  id: string;

  pageName: string;
  published: boolean;
  lang: (typeof supportedLocales)[number];

  createdAt: Date;
  updatedAt: Date;

  type: CmsPageType;

  content: C;
}

export interface CreateCmsPage<C> {
  pageName: string;
  published: boolean;
  content: C;
}

export interface UpdateCmsPage<C> {
  pageName: string;
  published?: boolean;
  content: C;
}

export const CrTypeSchema = (p?: z.RawCreateParams) =>
  z.enum(["ARTWORKS", "MULTIPRINTS"], p);

export const cmsPageSchema = z.object({
  id: z.string(),
  pageName: z
    .string()
    .trim()
    .nonempty({ message: "cms.form.error.pageName.empty" }),
  published: z.boolean().default(false),
  pageType: cmsPageTypeSchema,
});

export const baseCmsFormSchema = cmsPageSchema.pick({
  pageName: true,
  published: true,
});
export type BaseCmsForm = z.infer<typeof baseCmsFormSchema>;

export const homepageContentSchema = z.object({
  id: z.string().optional(),

  websiteTitle: z
    .string()
    .trim()
    .nonempty({ message: "cms.form.error.pageName.empty" }),

  websiteSubtitle: emptyToNullString(),
  artistName: emptyToNullString(),
  birthDate: emptyToNullString(),
  deathDate: emptyToNullString(),

  catalogueRaisonneCTA: emptyToNullString(),

  resume: emptyToNullString(),
  footerArchives: emptyToNullString(),
});

export type HomepageContent = z.infer<typeof homepageContentSchema>;

export const artistContentSchema = z.object({
  id: z.string().optional(),
  credits: emptyToNullString(),
  resumeArtist: emptyToNullString(),

  biographySectionTitle: emptyToNullString(),
  biographyContent: emptyToNullString(),
  publishedBiography: z.boolean().optional().default(false),

  collectionsSectionTitle: emptyToNullString(),
  collectionsContent: emptyToNullString(),
  publishedCollections: z.boolean().optional().default(false),

  exhibitionsSectionTitle: emptyToNullString(),
  exhibitionsContent: emptyToNullString(),
  publishedExhibitions: z.boolean().optional().default(false),

  bibliographySectionTitle: emptyToNullString(),
  bibliographyContent: emptyToNullString(),
  publishedBibliography: z.boolean().optional().default(false),
});

export type ArtistContent = z.infer<typeof artistContentSchema>;

export const selectionContentSchema = z.object({
  id: z.string().optional(),
  content: emptyToNullString(),
});

export type SelectionContent = z.infer<typeof selectionContentSchema>;

export const newsItemSchema = z.object({
  id: z.string().optional(),
  title: multilingualFieldSchema(z.string()).optional(),
  content: multilingualFieldSchema(z.string()).optional(),
  date: z.date(),

  attachments: z.array(attachmentSchema),

  updatedAt: z.string(),
  createdAt: z.string(),
});
export type NewsItem = z.infer<typeof newsItemSchema>;

export const newsPageContentSchema = z.object({
  id: z.string().optional(),
  content: emptyToNullString(),
  news: z.array(newsItemSchema),
});
export type NewsPageContent = z.infer<typeof newsPageContentSchema>;

export const newsPageEditContent = newsPageContentSchema.omit({
  news: true,
});

export type NewsPageEditContent = z.infer<typeof newsPageEditContent>;

export const expertiseContentSchema = z.object({
  id: z.string().optional(),

  title: emptyToNullString(),
  formDownloadCTA: emptyToNullString(),
  documentUrl: emptyToNullString(),
  content: emptyToNullString(),
});

export type ExpertiseContent = z.infer<typeof expertiseContentSchema>;

export const artworkAuthenticationContentSchema = z.object({
  id: z.string().optional(),
  title: z.string().optional(),
  content: z.string().optional(),
  honorDeclaration: z.string().optional(),
  certificationLegalStatement: z.string().optional(),
  homePageFile: z.string().optional().nullable(),
  sidebarFile: z.string().optional().nullable(),
  sidebarTitle: z.string().optional().nullable(),
});
export type ArtworkAuthenticationContent = z.infer<
  typeof artworkAuthenticationContentSchema
>;

export const aboutUsContentSchema = z.object({
  id: z.string().optional(),

  content: emptyToNullString(),

  credits: emptyToNullString(),

  creditsTitle: emptyToNullString(),
  creditsContent: emptyToNullString(),
  publishedCredits: z.boolean().default(false),

  legalNoticesTitle: emptyToNullString(),
  legalNoticesContent: emptyToNullString(),
  publishedLegalNotices: z.boolean().default(false),
});

export type AboutUsContent = z.infer<typeof aboutUsContentSchema>;

export const contactContentSchema = z.object({
  id: z.string().optional(),

  email: emptyToNullString(),
  content: emptyToNullString(),
});
export type ContactContent = z.infer<typeof contactContentSchema>;

export const loginContentSchema = z.object({
  id: z.string().optional(),
  subtitle: emptyToNullString(),
});
export type LoginContent = z.infer<typeof loginContentSchema>;

export const catalogueRaisonneGeneralContent = z.object({
  id: z.string().optional(),
  content: emptyToNullString(),
  artworkCTA: emptyToNullString(),
  multipleCTA: emptyToNullString(),
  showPublicAuctions: z.boolean().optional().default(false),
});
export type CatalogueRaisonneGeneralContent = z.infer<
  typeof catalogueRaisonneGeneralContent
>;

export const catalogueRaisonneSpecificContent = z.object({
  id: z.string().optional(),
  content: emptyToNullString(),
  runningTitle: emptyToNullString(),
  type: CrTypeSchema(),
});
export type CatalogueRaisonneSpecificContent = z.infer<
  typeof catalogueRaisonneSpecificContent
>;

export const signUpContentSchema = z.object({
  id: z.string().optional(),
  subtitle: emptyToNullString(),
});
export type SignUpContent = z.infer<typeof signUpContentSchema>;

export const privacyPolicyContentSchema = z.object({
  id: z.string().optional(),
  content: emptyToNullString(),
});
export type PrivacyPolicyContent = z.infer<typeof privacyPolicyContentSchema>;

export const legalNoticesContentSchema = z.object({
  id: z.string().optional(),
  content: emptyToNullString(),
});
export type LegalNoticesContent = z.infer<typeof legalNoticesContentSchema>;

export const gcuContentSchema = z.object({
  id: z.string().optional(),
  content: emptyToNullString(),
});
export type GcuContent = z.infer<typeof gcuContentSchema>;

export const eshopContentSchema = z.object({
  id: z.string().optional(),
});
export type EShopContent = z.infer<typeof eshopContentSchema>;

export const catalogueRaisonneSectionSchema = z.object({
  id: z.string(),
  nameMultilingual: z.object(
    supportedLocales.reduce(
      (acc, locale) => ({ ...acc, [locale]: z.string() }),
      {},
    ),
  ),
  contentMultilingual: z
    .object(
      supportedLocales.reduce(
        (acc, locale) => ({ ...acc, [locale]: z.string() }),
        {},
      ),
    )
    .optional(),
  titleMultilingual: z.object(
    supportedLocales.reduce(
      (acc, locale) => ({ ...acc, [locale]: z.string() }),
      {},
    ),
  ).optional(),
  showContent: z.boolean().optional(),
  order: z.number(),
  createdAt: z.date(),
  updatedAt: z.date(),
  cmsId: z.string(),
  type: z.string(),
  startValue: z.string().nullable(),
  endValue: z.string().nullable(),
  categoryId: z.string().nullable(),
  categoryMultilingual: multilingualFieldSchema(z.string()).optional(),
  target: CrTypeSchema(),
  vignette: attachmentSchema.nullable(),
});
export type CatalogueRaisonneSection = z.infer<
  typeof catalogueRaisonneSectionSchema
>;

export const catalogueRaisonneSectionUpsertSchema =
  catalogueRaisonneSectionSchema.pick({
    nameMultilingual: true,
    titleMultilingual: true,
    contentMultilingual: true,
    showContent: true,
    startValue: true,
    endValue: true,
    categoryId: true,
    categoryMultilingual: true,
    vignette: true,
    order: true,
    type: true,
  });

export type CatalogueRaisonneSectionUpsert = z.infer<
  typeof catalogueRaisonneSectionUpsertSchema
>;

export const socialTypes = [
  "FACEBOOK",
  "INSTAGRAM",
  "YOUTUBE",
  "LINKEDIN",
] as const;

export const socialTypeSchema = z.enum(socialTypes);

export type SocialType = z.infer<typeof socialTypeSchema>;

export const socialSchema = z.object({
  socialType: socialTypeSchema,
  url: z.string().nonempty({ message: "cms.form.error.url.empty" }),
  visible: z.boolean().default(false),
  colored: z.boolean().default(true),
});

export type Social = z.infer<typeof socialSchema>;

export const generalSettingsSchema = z.object({
  id: z.string().optional(),
  headerAlwaysVisible: z.boolean().default(false),
  headerAlwaysVisibleMobile: z.boolean().default(false),
  socials: z.array(socialSchema),
});

export type GeneralSettings = z.infer<typeof generalSettingsSchema>;
